import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
  type Ticker,
} from 'pixi.js';
import { playSfx } from '../effects/sound.js';
import { usePixiScene } from './pixi/usePixiScene.js';
import { mixColor, originalTheme, sceneColor, tierColors } from '../theme.js';
import type { SceneProps } from './types.js';
import { preferredDensity, preloadPieces, type PieceImages } from '../assets/sprites.js';
import { COIN_FRAMES, WHEEL_PIECES, artScale, bulbPositions, pinTipY, placeLayer, placeWedge, wedgePiece, type WheelPiece } from './wheel/wheelArt.js';

interface WheelSegment {
  label: string;
  color: number;
  win: boolean;
}

interface WheelPresentation {
  segmentIndex: number;
}

const FALLBACK_COLORS = [
  0x2b2640, 0x4f5bd5, 0x6d5cf5, 0x8b5cf6, 0xa855f7, 0xc026d3, 0xec4899,
  0xfb7185,
];

const PRIMARY_FALLBACK = 0x7c3aed;
const GOLD = 0xfbbf24;

// Per-risk accent: the wheel "feels" different per table — calm emerald on
// low, brand violet on med, hot ember on high. Applied to rim/pointer/rays.
const RISK_ACCENTS: Record<string, number> = {
  low: 0x34d399,
  high: 0xff7a3d,
};

const SPIN_TURNS = 5;
const SPIN_DURATION_MS = 4500;
const SPIN_DURATION_REDUCED_MS = 2500;
const TURBO_DURATION_SCALE = 0.3;
const IDLE_SPEED = 0.00045; // rad/ms
const BULB_COUNT = 24;
const MIN_TICK_INTERVAL_MS = 55;
// Above this slice count, per-wedge text is too cramped to read — show colour-only
// wedges and rely on the multiplier legend rendered below the wheel.
const LABEL_MAX_SEGMENTS = 14;
const CELEBRATE_MS = 1500;
const BIG_WIN_MULTIPLIER = 5;
// Anticipation: wind back briefly before launching forward.
const BACKSWING_FRAC = 0.09;
// Suspense window: the last stretch of the spin gets a spotlight + heartbeat.
const SUSPENSE_FROM = 0.84;
const SPARK_POOL = 18;
const CONFETTI_POOL = 130;

function parseHexColor(input: string | undefined, fallback: number): number {
  if (!input) return fallback;
  const hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return parseInt(hex, 16);
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return parseInt(`${r}${r}${g}${g}${b}${b}`, 16);
  }
  return fallback;
}

function isWinLabel(label: string): boolean {
  const n = Number.parseFloat(label);
  return Number.isFinite(n) && n > 0;
}

function readSegments(presenterConfig: unknown): WheelSegment[] {
  const ring =
    (presenterConfig as { ring?: unknown[] } | null | undefined)?.ring ?? [];
  if (!Array.isArray(ring) || ring.length === 0) {
    return FALLBACK_COLORS.map((color, i) => ({
      label: `${i + 1}x`,
      color,
      win: true,
    }));
  }
  const slices = ring.map((slice) => {
    const obj =
      slice != null && typeof slice === 'object'
        ? (slice as { label?: unknown; color?: unknown; multiplierBp?: unknown })
        : {};
    return {
      label: typeof obj.label === 'string' ? obj.label : '',
      color: typeof obj.color === 'string' ? obj.color : '',
      multiplierBp: typeof obj.multiplierBp === 'number' ? obj.multiplierBp : null,
    };
  });
  // Tier colours need every slice's multiplier; a ring without them keeps the server's colours.
  const ranked = slices.every((sl) => sl.multiplierBp !== null)
    ? tierColors(
        slices.map((sl) => ({ ...sl, multiplierBp: sl.multiplierBp ?? 0 })),
        originalTheme('wheel')?.tiers,
      )
    : slices;
  return ranked.map((sl, i) => ({
    label: sl.label,
    color: parseHexColor(sl.color || undefined, FALLBACK_COLORS[i % FALLBACK_COLORS.length]),
    win: isWinLabel(sl.label),
  }));
}

// The stage-dark parts of the wheel (rim, hub, dividers) in the game's own deep
// colour, so the wheel sits in the tile's world rather than on a fixed violet-black.
function wheelTones() {
  const theme = originalTheme('wheel');
  if (!theme) {
    return { divider: 0x100c1a, edge: 0x0d0a16, rim: 0x161320, cap: 0x120f1c, well: 0x171226 };
  }
  const deep = sceneColor(theme.deep);
  return {
    divider: mixColor(deep, 0x000000, 0.45),
    edge: mixColor(deep, 0x000000, 0.55),
    rim: deep,
    cap: mixColor(deep, 0x000000, 0.25),
    well: mixColor(deep, 0xffffff, 0.04),
  };
}

/** Fresh, uncached textures per scene: the scene's teardown destroys them, the decoded images stay cached. */
function toTextures(imgs: PieceImages<WheelPiece>): Record<WheelPiece, Texture> {
  const out = {} as Record<WheelPiece, Texture>;
  for (const piece of WHEEL_PIECES) out[piece] = Texture.from({ resource: imgs[piece], autoGenerateMipmaps: true }, true);
  return out;
}

const COIN_POOL = 18;

function readSegmentIndex(presentation: unknown): number {
  if (
    presentation != null &&
    typeof presentation === 'object' &&
    'segmentIndex' in presentation
  ) {
    const idx = (presentation as WheelPresentation).segmentIndex;
    if (typeof idx === 'number' && Number.isFinite(idx)) return idx;
  }
  return 0;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}

function darken(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const k = 1 - amount;
  return (
    (Math.round(r * k) << 16) | (Math.round(g * k) << 8) | Math.round(b * k)
  );
}

// A smooth radial-gradient disc texture. Pixi's huge-radius Graphics circles
// tessellate visibly (the old halo rendered as an octagon); a canvas gradient
// sprite is perfectly round and doubles as a soft particle/vignette texture.
function makeGlowTexture(size = 256): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.WHITE;
  const half = size / 2;
  const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(0.7, 'rgba(255,255,255,0.16)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(canvas);
}

// Which segment sits under the top pointer for a given disc rotation (radians).
// Wedge i occupies local angles [i*slice - PI/2, (i+1)*slice - PI/2]; with
// disc.rotation = R a local angle θ renders at θ + R. The top pointer reads
// screen angle -PI/2, so the wedge there satisfies i*slice ≤ -R (mod 2PI),
// i.e. i = floor(((-R) mod 2PI) / slice). Landing target R = -(i+0.5)*slice
// makes wedge i's centre line up with the pointer (see startSpin).
function segmentUnderPointer(
  rotation: number,
  segCount: number,
  slice: number,
): number {
  const norm = ((-rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return Math.floor(norm / slice) % segCount;
}

interface SceneState {
  phase: SceneProps['phase'];
  envelope: SceneProps['envelope'];
  onRevealComplete: () => void;
  turbo: boolean;
}

type Celebration = 'none' | 'win' | 'bigwin' | 'lose';

interface FxParticle {
  g: Graphics | Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  life: number;
  maxLife: number;
  active: boolean;
}

export function WheelScene({
  config,
  envelope,
  phase,
  onRevealComplete,
  turbo = false,
}: SceneProps) {
  const { t } = useTranslation('originals');

  const segments = useMemo(
    () => readSegments(config.presenterConfig),
    [config.presenterConfig],
  );

  const risk = String(config.resolvedParams?.risk ?? 'med');

  // Live props bridged into the imperative Pixi ticker via a ref.
  const stateRef = useRef<SceneState>({ phase, envelope, onRevealComplete, turbo });
  stateRef.current = { phase, envelope, onRevealComplete, turbo };

  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const riskRef = useRef(risk);
  riskRef.current = risk;

  // Stable onReady — captured once per mount by usePixiScene.
  const onReady = useRef((app: Application, host: HTMLDivElement) => {
    const segs = segmentsRef.current;
    const segCount = segs.length || 1;
    const slice = (Math.PI * 2) / segCount;

    const primary = parseHexColor(
      getComputedStyle(host).getPropertyValue('--primary').trim() || undefined,
      PRIMARY_FALLBACK,
    );
    const accent = RISK_ACCENTS[riskRef.current] ?? primary;
    const tones = wheelTones();
    const accentGlow = lighten(accent, 0.4);
    const glowTexture = makeGlowTexture();

    const root = new Container();
    app.stage.addChild(root);

    const backdrop = new Sprite(glowTexture); // smooth halo behind the wheel
    backdrop.anchor.set(0.5);
    backdrop.tint = accent;
    const rays = new Graphics(); // win light-rays burst (behind rim)
    const rim = new Container(); // static: outer ring + bulbs
    const disc = new Container(); // rotates: wedges, vignette, dividers, labels
    const wedges = new Container(); // disc layer 1: coloured sectors
    const vignette = new Sprite(glowTexture); // disc layer 2: smooth depth toward hub
    vignette.anchor.set(0.5);
    vignette.tint = 0x000000;
    const glow = new Graphics(); // disc layer 3: landing / near-miss highlight
    const labelLayer = new Container(); // disc layer 4: per-wedge multiplier text
    const discEdge = new Graphics(); // disc layer 5: finished outer edge
    const sheen = new Graphics(); // idle sheen sweeping over the disc
    disc.addChild(wedges, vignette, glow, labelLayer, discEdge, sheen);
    const suspense = new Graphics(); // late-spin spotlight (screen space, on top of disc)
    const fx = new Container(); // sparks + confetti + dust
    const hub = new Container(); // static: centre cap + live label
    const pointer = new Container(); // static: top marker (glow + blade)
    const pointerGlow = new Graphics();
    const pointerBlade = new Graphics();
    pointer.addChild(pointerGlow, pointerBlade);

    // The 3D render's still layers: the back under the disc, the shade, gloss, rim and bulbs over it.
    const artBack = new Container();
    const artFront = new Container();
    let pin: Sprite | null = null;
    let tex: Record<WheelPiece, Texture> | null = null;
    let disposed = false;
    const artOn = () => tex !== null && wedgePiece(segCount) !== null;

    root.addChild(backdrop, rays, artBack, rim, disc, artFront, suspense, fx, hub, pointer);

    // ----- imperative animation state -----
    let radius = 100;
    let discRotation = 0;
    let spinning = false;
    let spinStartRotation = 0;
    let spinTargetRotation = 0;
    let spinElapsed = 0;
    let spinDuration = SPIN_DURATION_MS;
    let backswing = 0; // radians wound back before launch (0 = disabled)
    let landedIndex: number | null = null;
    let lastTickSeg: number | null = null;
    let lastTickAt = 0;
    let startedRoundId: string | null = null;
    let firedRoundId: string | null = null;
    let heartbeatPlayed = false;
    let pointerPulse = 0;
    let launchSquash = 0; // disc squash on launch, decays
    let celebration: Celebration = 'none';
    let celebrationElapsed = 0;
    let labelPop = 0; // hub label bounce timer, decays
    let shake = 0; // screen-shake magnitude (px), decays each frame

    const bulbs: Container[] = [];
    const labels: Text[] = [];

    // Hub readout: shows the segment streaming under the pointer while the
    // wheel spins and locks onto the result — the round is readable at a
    // glance without hunting the rim.
    const hubLabel = new Text({
      text: '',
      style: {
        fontFamily: 'system-ui, sans-serif',
        fontSize: 16,
        fill: 0xffffff,
        fontWeight: '900',
        align: 'center',
      },
    });
    hubLabel.anchor.set(0.5);

    // ----- particle pools -----
    const sparks: FxParticle[] = [];
    for (let i = 0; i < SPARK_POOL; i++) {
      const s = new Sprite(glowTexture);
      s.anchor.set(0.5);
      s.visible = false;
      fx.addChild(s);
      sparks.push({ g: s, x: 0, y: 0, vx: 0, vy: 0, rot: 0, vr: 0, life: 0, maxLife: 1, active: false });
    }
    const confetti: FxParticle[] = [];
    for (let i = 0; i < CONFETTI_POOL; i++) {
      const g = new Graphics();
      g.rect(-4, -2, 8, 4).fill({ color: 0xffffff });
      g.visible = false;
      fx.addChild(g);
      confetti.push({ g, x: 0, y: 0, vx: 0, vy: 0, rot: 0, vr: 0, life: 0, maxLife: 1, active: false });
    }

    // Gold coins thrown off a win (the 3D render's spinning coin), drawn only with the art.
    const coins: (FxParticle & { frame: number; spin: number })[] = [];
    for (let i = 0; i < COIN_POOL; i++) {
      const s = new Sprite(Texture.EMPTY);
      s.anchor.set(0.5);
      s.visible = false;
      fx.addChild(s);
      coins.push({ g: s, x: 0, y: 0, vx: 0, vy: 0, rot: 0, vr: 0, life: 0, maxLife: 1, active: false, frame: 0, spin: 0 });
    }

    function burstCoins(big: boolean) {
      if (!tex) return;
      const speedScale = radius / 260;
      const n = big ? COIN_POOL : Math.round(COIN_POOL * 0.6);
      for (let i = 0; i < n; i++) {
        const p = coins[i]!;
        const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.6;
        const speed = (0.25 + Math.random() * 0.35) * speedScale;
        p.active = true;
        p.x = (Math.random() - 0.5) * radius * 0.2;
        p.y = (Math.random() - 0.5) * radius * 0.2;
        p.vx = Math.cos(a) * speed;
        p.vy = Math.sin(a) * speed;
        p.life = 0;
        p.maxLife = 1100 + Math.random() * 600;
        p.frame = Math.random() * COIN_FRAMES;
        p.spin = 0.012 + Math.random() * 0.012;
        const s = p.g as Sprite;
        s.texture = tex[`coin-${Math.floor(p.frame)}` as WheelPiece];
        s.width = s.height = radius * (0.11 + Math.random() * 0.05);
        s.visible = true;
        s.alpha = 1;
      }
    }

    function spawnSpark(x: number, y: number, color: number) {
      const p = sparks.find((s) => !s.active);
      if (!p) return;
      p.active = true;
      p.x = x;
      p.y = y;
      p.vx = (Math.random() - 0.5) * 0.22;
      p.vy = 0.05 + Math.random() * 0.16;
      p.life = 0;
      p.maxLife = 320 + Math.random() * 200;
      const s = p.g as Sprite;
      s.tint = color;
      s.visible = true;
      s.alpha = 0.9;
      const sc = (radius / 260) * (0.05 + Math.random() * 0.05);
      s.scale.set(sc);
    }

    function burstConfetti(big: boolean) {
      const palette = big
        ? [GOLD, 0xffe08a, accentGlow, 0xffffff, lighten(accent, 0.2)]
        : segs.filter((s) => s.win).map((s) => lighten(s.color, 0.25)).concat(0xffffff);
      const count = big ? CONFETTI_POOL : Math.floor(CONFETTI_POOL * 0.55);
      const speedScale = radius / 260;
      let spawned = 0;
      for (const p of confetti) {
        if (spawned >= count) break;
        p.active = true;
        spawned++;
        const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
        const speed = (0.18 + Math.random() * 0.4) * speedScale;
        p.x = (Math.random() - 0.5) * radius * 0.5;
        p.y = -radius * (0.95 + Math.random() * 0.2);
        p.vx = Math.cos(a) * speed * (Math.random() < 0.5 ? 1 : -1);
        p.vy = Math.sin(a) * speed * 0.6;
        p.rot = Math.random() * Math.PI * 2;
        p.vr = (Math.random() - 0.5) * 0.02;
        p.life = 0;
        p.maxLife = 1300 + Math.random() * 700;
        const g = p.g as Graphics;
        g.tint = palette[Math.floor(Math.random() * palette.length)]!;
        g.visible = true;
        g.alpha = 1;
        g.scale.set(speedScale * (0.7 + Math.random() * 0.7));
      }
    }

    function puffDust() {
      const speedScale = radius / 260;
      let spawned = 0;
      for (const p of confetti) {
        if (spawned >= 10) break;
        p.active = true;
        spawned++;
        p.x = (Math.random() - 0.5) * radius * 0.25;
        p.y = -radius * 0.9;
        p.vx = (Math.random() - 0.5) * 0.08 * speedScale;
        p.vy = (0.04 + Math.random() * 0.08) * speedScale;
        p.rot = Math.random() * Math.PI;
        p.vr = (Math.random() - 0.5) * 0.006;
        p.life = 0;
        p.maxLife = 700 + Math.random() * 400;
        const g = p.g as Graphics;
        g.tint = 0x6b7280;
        g.visible = true;
        g.alpha = 0.55;
        g.scale.set(speedScale * (0.5 + Math.random() * 0.4));
      }
    }

    function stepParticles(dt: number) {
      const gravity = 0.00075 * (radius / 260);
      for (const p of sparks) {
        if (!p.active) continue;
        p.life += dt;
        if (p.life >= p.maxLife) {
          p.active = false;
          p.g.visible = false;
          continue;
        }
        p.vy += gravity * dt * 0.6;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.g.position.set(p.x, p.y);
        p.g.alpha = 0.9 * (1 - p.life / p.maxLife);
      }
      for (const p of confetti) {
        if (!p.active) continue;
        p.life += dt;
        if (p.life >= p.maxLife) {
          p.active = false;
          p.g.visible = false;
          continue;
        }
        p.vy += gravity * dt;
        p.vx *= Math.pow(0.999, dt);
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.g.position.set(p.x, p.y);
        p.g.rotation = p.rot;
        const fade = 1 - p.life / p.maxLife;
        p.g.alpha = Math.min(1, fade * 1.6);
      }
    }

    function stepCoins(dt: number) {
      const gravity = 0.0011 * (radius / 260);
      for (const p of coins) {
        if (!p.active || !tex) continue;
        p.life += dt;
        if (p.life >= p.maxLife) {
          p.active = false;
          p.g.visible = false;
          continue;
        }
        p.vy += gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.frame = (p.frame + p.spin * dt) % COIN_FRAMES;
        const s = p.g as Sprite;
        s.texture = tex[`coin-${Math.floor(p.frame)}` as WheelPiece];
        s.position.set(p.x, p.y);
        s.alpha = Math.min(1, (1 - p.life / p.maxLife) * 2.5);
      }
    }

    function buildLabels() {
      for (const l of labels) l.destroy();
      labels.length = 0;
      if (segCount > LABEL_MAX_SEGMENTS) return; // too dense — legend below shows the multipliers
      for (let i = 0; i < segCount; i++) {
        const seg = segs[i];
        const label = new Text({
          text: seg?.label ?? '',
          style: {
            fontFamily: 'system-ui, sans-serif',
            fontSize: Math.max(13, Math.round(radius * 0.1)),
            fill: 0xffffff,
            fontWeight: '800',
            align: 'center',
            dropShadow: {
              color: 0x000000,
              alpha: 0.45,
              blur: 2,
              distance: 1,
              angle: Math.PI / 2,
            },
          },
        });
        label.anchor.set(0.5);
        labelLayer.addChild(label);
        labels.push(label);
      }
    }

    function layoutLabels() {
      const labelRadius = radius * 0.68;
      for (let i = 0; i < labels.length; i++) {
        const mid = i * slice + slice / 2 - Math.PI / 2;
        const label = labels[i];
        label.position.set(
          Math.cos(mid) * labelRadius,
          Math.sin(mid) * labelRadius,
        );
        label.rotation = mid + Math.PI / 2;
        label.style.fontSize = Math.max(13, Math.round(radius * 0.1));
      }
    }

    // Annular sector path between two radii across one wedge.
    function annularSector(
      g: Graphics,
      rIn: number,
      rOut: number,
      a0: number,
      a1: number,
    ) {
      g.moveTo(Math.cos(a0) * rIn, Math.sin(a0) * rIn)
        .lineTo(Math.cos(a0) * rOut, Math.sin(a0) * rOut)
        .arc(0, 0, rOut, a0, a1)
        .lineTo(Math.cos(a1) * rIn, Math.sin(a1) * rIn)
        .arc(0, 0, rIn, a1, a0, true);
    }

    function drawDisc() {
      wedges.removeChildren().forEach((c) => c.destroy());
      const piece = wedgePiece(segCount);
      if (tex && piece) {
        for (let i = 0; i < segCount; i++) {
          const color = segs[i]?.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length]!;
          wedges.addChild(placeWedge(tex[piece], segCount, i, color, radius));
        }
        vignette.alpha = 0;
        discEdge.clear();
        return;
      }
      for (let i = 0; i < segCount; i++) {
        const a0 = i * slice - Math.PI / 2;
        const a1 = (i + 1) * slice - Math.PI / 2;
        const color = segs[i]?.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length];
        const wedge = new Graphics();
        // base fill
        wedge.moveTo(0, 0).arc(0, 0, radius, a0, a1).lineTo(0, 0).fill({ color });
        // glossy outer band near the rim
        annularSector(wedge, radius * 0.82, radius, a0, a1);
        wedge.fill({ color: lighten(color, 0.3), alpha: 0.35 });
        // divider stroke on the leading edge (shared with the next wedge)
        wedge
          .moveTo(0, 0)
          .lineTo(Math.cos(a0) * radius, Math.sin(a0) * radius)
          .stroke({ width: Math.max(1, radius * 0.008), color: tones.divider, alpha: 0.5 });
        wedges.addChild(wedge);
      }
      // smooth radial depth toward the hub (replaces the old hard-edged dark
      // band that read as a flat inner ring)
      vignette.width = radius * 2.3;
      vignette.height = radius * 2.3;
      vignette.alpha = 0.42;
      // finished outer edge
      discEdge
        .clear()
        .circle(0, 0, radius)
        .stroke({ width: Math.max(2, radius * 0.025), color: tones.edge, alpha: 0.55 });
    }

    function drawBackdrop() {
      const d = radius * 3.4;
      backdrop.width = d;
      backdrop.height = d;
      backdrop.alpha = 0.32;
    }

    function drawRim() {
      rim.removeChildren().forEach((c) => c.destroy());
      artBack.removeChildren().forEach((c) => c.destroy());
      artFront.removeChildren().forEach((c) => c.destroy());
      bulbs.length = 0;
      if (tex && artOn()) {
        artBack.addChild(placeLayer(tex.back, 'back', radius));
        const gloss = placeLayer(tex.gloss, 'gloss', radius);
        gloss.blendMode = 'add';
        artFront.addChild(placeLayer(tex.shade, 'shade', radius), gloss, placeLayer(tex.rim, 'rim', radius));
        const k = artScale(radius);
        for (const { x, y } of bulbPositions(radius)) {
          // A holder per bulb: the ticker scales and fades it like the drawn bulbs.
          const holder = new Container();
          const light = new Sprite(tex.glow);
          light.anchor.set(0.5);
          light.width = light.height = 64 * k;
          light.blendMode = 'add';
          holder.addChild(light);
          holder.position.set(x, y);
          artFront.addChild(holder);
          bulbs.push(holder);
        }
        return;
      }
      const ringR = radius + radius * 0.07;
      const ring = new Graphics();
      ring
        .circle(0, 0, ringR)
        .fill({ color: tones.rim, alpha: 1 })
        .circle(0, 0, ringR)
        .stroke({ width: Math.max(2, radius * 0.03), color: accent, alpha: 0.5 })
        .circle(0, 0, radius + radius * 0.02)
        .stroke({ width: Math.max(1, radius * 0.012), color: accentGlow, alpha: 0.25 });
      rim.addChild(ring);
      const bulbR = ringR - radius * 0.045;
      for (let i = 0; i < BULB_COUNT; i++) {
        const a = (i / BULB_COUNT) * Math.PI * 2 - Math.PI / 2;
        const bulb = new Graphics();
        bulb
          .circle(0, 0, Math.max(2, radius * 0.028))
          .fill({ color: 0xffffff, alpha: 1 });
        bulb.position.set(Math.cos(a) * bulbR, Math.sin(a) * bulbR);
        rim.addChild(bulb);
        bulbs.push(bulb);
      }
    }

    function drawHub() {
      hub.removeChildren().forEach((c) => {
        if (c !== hubLabel) c.destroy();
      });
      if (tex && artOn()) {
        hubLabel.style.fontSize = Math.max(13, Math.round(radius * 0.105));
        hubLabel.style.stroke = { color: 0x3a0a3a, width: Math.max(3, radius * 0.018), join: 'round' };
        hub.addChild(placeLayer(tex.hub, 'hub', radius), hubLabel);
        return;
      }
      const hr = radius * 0.2;
      // Strictly concentric stack — cap, accent ring, inner well, gloss arc.
      // (The old offset "shine" blob made the whole hub read as off-axis.)
      const cap = new Graphics();
      cap
        .circle(0, 0, hr * 1.22)
        .fill({ color: tones.cap, alpha: 1 })
        .circle(0, 0, hr * 1.22)
        .stroke({ width: Math.max(1, radius * 0.01), color: accentGlow, alpha: 0.35 });
      const ring = new Graphics();
      ring
        .circle(0, 0, hr)
        .fill({ color: darken(accent, 0.25), alpha: 1 })
        .circle(0, 0, hr)
        .stroke({ width: Math.max(2, radius * 0.016), color: accentGlow, alpha: 0.9 });
      const well = new Graphics();
      well.circle(0, 0, hr * 0.78).fill({ color: tones.well, alpha: 1 });
      // symmetric top gloss — an arc, not an offset blob
      const gloss = new Graphics();
      gloss
        .arc(0, 0, hr * 0.92, -Math.PI * 0.82, -Math.PI * 0.18)
        .stroke({ width: Math.max(1.5, radius * 0.012), color: 0xffffff, alpha: 0.22 });
      hubLabel.style.fontSize = Math.max(13, Math.round(radius * 0.105));
      hub.addChild(cap, ring, well, gloss, hubLabel);
    }

    function drawPointer() {
      pointerBlade.clear();
      pointerGlow.clear();
      pin?.destroy();
      pin = null;
      if (tex && artOn()) {
        pin = placeLayer(tex.pin, 'pin', radius);
        pointer.addChild(pin);
        // The pin rocks on its point, where it touches the wheel.
        const tip = pinTipY(radius);
        pointer.pivot.set(0, tip);
        pointer.position.set(0, tip);
        return;
      }
      const w = radius * 0.085;
      const top = -(radius + radius * 0.07) - radius * 0.05;
      const bottom = -(radius) + radius * 0.04;
      pointerGlow
        .moveTo(-w * 1.5, top - radius * 0.015)
        .lineTo(w * 1.5, top - radius * 0.015)
        .lineTo(0, bottom + radius * 0.03)
        .closePath()
        .fill({ color: accentGlow, alpha: 0.2 });
      pointerBlade
        .moveTo(-w, top)
        .lineTo(w, top)
        .lineTo(0, bottom)
        .closePath()
        .fill({ color: accentGlow, alpha: 1 })
        .stroke({ width: Math.max(1, radius * 0.01), color: 0xffffff, alpha: 0.55 });
      pointerBlade
        .circle(0, top + radius * 0.035, radius * 0.026)
        .fill({ color: 0xffffff, alpha: 0.7 });
      pointer.pivot.set(0, bottom);
      pointer.position.set(0, bottom);
    }

    function relayout() {
      const min = Math.min(app.screen.width, app.screen.height);
      // The 3D pin stands taller above the rim than the drawn one; the wheel gives it room.
      radius = Math.max(40, (min / 2) * (artOn() ? 0.74 : 0.8));
      root.position.set(app.screen.width / 2, app.screen.height / 2);
      drawDisc();
      buildLabels();
      layoutLabels();
      drawBackdrop();
      drawRim();
      drawHub();
      drawPointer();
    }

    relayout();

    const onResize = () => relayout();
    app.renderer.on('resize', onResize);

    function startSpin(roundId: string, segmentIndex: number) {
      startedRoundId = roundId;
      landedIndex = segmentIndex;
      celebration = 'none';
      celebrationElapsed = 0;
      heartbeatPlayed = false;
      glow.clear();
      suspense.clear();
      spinning = true;
      spinElapsed = 0;
      spinStartRotation = discRotation;
      const reduced = prefersReducedMotion();
      const baseDuration = reduced ? SPIN_DURATION_REDUCED_MS : SPIN_DURATION_MS;
      spinDuration = stateRef.current.turbo
        ? baseDuration * TURBO_DURATION_SCALE
        : baseDuration;
      backswing = reduced || stateRef.current.turbo ? 0 : Math.min(0.45, slice * 0.9);
      launchSquash = backswing > 0 ? 1 : 0;

      // Land the centre of `segmentIndex` under the top pointer. Wedge i centre
      // is at local angle (i+0.5)*slice - PI/2; the pointer reads the top
      // (-PI/2). Required disc rotation R (mod 2PI): R == -(i+0.5)*slice.
      const targetMod = -((segmentIndex + 0.5) * slice);
      const turns = SPIN_TURNS * Math.PI * 2;
      const base = spinStartRotation + turns;
      const baseMod = ((base % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const wantMod = ((targetMod % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      let delta = wantMod - baseMod;
      if (delta < 0) delta += Math.PI * 2;
      spinTargetRotation = base + delta;

      lastTickSeg = segmentUnderPointer(discRotation, segCount, slice);
      lastTickAt = 0;
      playSfx('spinStart');
    }

    function startCelebration(env: NonNullable<SceneState['envelope']>) {
      celebrationElapsed = 0;
      labelPop = 1;
      const reduced = prefersReducedMotion();
      if (env.win) {
        const mult = Number.parseFloat(env.multiplier);
        celebration = mult >= BIG_WIN_MULTIPLIER ? 'bigwin' : 'win';
        shake = celebration === 'bigwin' ? radius * 0.045 : radius * 0.02;
        if (!reduced) burstConfetti(celebration === 'bigwin');
        if (!reduced && artOn()) burstCoins(celebration === 'bigwin');
        playSfx(celebration === 'bigwin' ? 'bigWin' : 'win');
      } else {
        celebration = 'lose';
        shake = radius * 0.025; // a sharp jolt — losing still kicks
        if (!reduced) puffDust();
        playSfx('lose');
      }
    }

    function fireReveal(roundId: string) {
      if (firedRoundId === roundId) return;
      firedRoundId = roundId;
      playSfx('reveal');
      stateRef.current.onRevealComplete();
    }

    function wedgeGlow(index: number, color: number, alpha: number) {
      const a0 = index * slice - Math.PI / 2;
      const a1 = (index + 1) * slice - Math.PI / 2;
      glow
        .moveTo(0, 0)
        .arc(0, 0, radius, a0, a1)
        .lineTo(0, 0)
        .fill({ color, alpha });
    }

    function drawCelebrationGlow(progress: number) {
      glow.clear();
      if (landedIndex == null) return;
      if (celebration === 'win' || celebration === 'bigwin') {
        // steady bright wedge with a pulse
        const pulse = 0.5 + 0.5 * Math.abs(Math.sin(progress * Math.PI * 3));
        const c = celebration === 'bigwin' ? GOLD : accentGlow;
        wedgeGlow(landedIndex, c, 0.35 + 0.35 * pulse);
      } else if (celebration === 'lose') {
        // near-miss tease: flash the adjacent WINNING wedges ("เกือบแล้ว!")
        const tease = Math.max(0, 1 - progress) * (0.5 + 0.5 * Math.sin(progress * Math.PI * 6));
        for (const d of [-1, 1]) {
          const idx = ((landedIndex + d) % segCount + segCount) % segCount;
          if (segs[idx]?.win) wedgeGlow(idx, lighten(segs[idx]!.color, 0.4), 0.5 * tease);
        }
        // dim flash over the losing wedge itself
        wedgeGlow(landedIndex, 0xffffff, 0.12 * Math.max(0, 1 - progress * 2));
      }
    }

    function drawRays(progress: number) {
      rays.clear();
      if (celebration !== 'win' && celebration !== 'bigwin') return;
      const fade = Math.max(0, 1 - progress);
      if (fade <= 0) return;
      const count = 14;
      const rOut = radius * 1.55;
      const color = celebration === 'bigwin' ? GOLD : accentGlow;
      rays.rotation = progress * 0.6;
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2;
        const half = (Math.PI / count) * 0.45;
        rays
          .moveTo(0, 0)
          .lineTo(Math.cos(a - half) * rOut, Math.sin(a - half) * rOut)
          .lineTo(Math.cos(a + half) * rOut, Math.sin(a + half) * rOut)
          .closePath()
          .fill({ color, alpha: 0.18 * fade });
      }
    }

    // Late-spin spotlight: everything except the wedge under the pointer dims,
    // so the final crawl reads as "which one will it be" instead of a blur.
    function drawSuspense(strength: number) {
      suspense.clear();
      if (strength <= 0) return;
      const current = segmentUnderPointer(discRotation, segCount, slice);
      for (let i = 0; i < segCount; i++) {
        if (i === current) continue;
        const a0 = i * slice - Math.PI / 2 + discRotation;
        const a1 = (i + 1) * slice - Math.PI / 2 + discRotation;
        suspense
          .moveTo(0, 0)
          .arc(0, 0, radius, a0, a1)
          .lineTo(0, 0)
          .fill({ color: 0x05030a, alpha: 0.32 * strength });
      }
    }

    // Idle sheen: a soft highlight slowly sweeping the disc face.
    function drawSheen(now: number, visible: boolean) {
      sheen.clear();
      if (!visible) return;
      const a = (now / 4200) % (Math.PI * 2);
      annularSector(sheen, radius * 0.3, radius * 0.98, a, a + 0.7);
      sheen.fill({ color: 0xffffff, alpha: 0.045 });
    }

    function setHubLabel(text: string, color: number) {
      hubLabel.text = text;
      hubLabel.style.fill = color;
    }

    const tick = (tk: Ticker) => {
      const dt = tk.deltaMS;
      const now = performance.now();
      const { phase: ph, envelope: env } = stateRef.current;

      // Begin a spin when we enter `playing` with an unstarted envelope.
      if (ph === 'playing' && env && startedRoundId !== env.roundId && !spinning) {
        startSpin(env.roundId, readSegmentIndex(env.outcome.presentation));
      }

      let suspenseStrength = 0;

      if (spinning) {
        spinElapsed += dt;
        const tNorm = Math.min(1, spinElapsed / spinDuration);

        // Anticipation backswing, then the long ease-out to the target.
        if (backswing > 0 && tNorm < BACKSWING_FRAC) {
          const b = easeOutQuad(tNorm / BACKSWING_FRAC);
          discRotation = spinStartRotation - backswing * b;
        } else {
          const from = spinStartRotation - backswing;
          const span = backswing > 0
            ? (tNorm - BACKSWING_FRAC) / (1 - BACKSWING_FRAC)
            : tNorm;
          const eased = easeOutQuart(Math.max(0, span));
          discRotation = from + (spinTargetRotation - from) * eased;
        }

        // throttled per-segment tick (skip during the backswing)
        const seg = segmentUnderPointer(discRotation, segCount, slice);
        if (
          lastTickSeg != null &&
          seg !== lastTickSeg &&
          spinElapsed - lastTickAt >= MIN_TICK_INTERVAL_MS &&
          (backswing === 0 || spinElapsed / spinDuration >= BACKSWING_FRAC)
        ) {
          lastTickAt = spinElapsed;
          playSfx('tick');
          pointerPulse = 1;
          if (!prefersReducedMotion()) {
            const tipY = -(radius + radius * 0.02);
            spawnSpark((Math.random() - 0.5) * radius * 0.06, tipY, 0xffffff);
            spawnSpark((Math.random() - 0.5) * radius * 0.06, tipY, accentGlow);
          }
        }
        lastTickSeg = seg;

        // live readout of whatever streams under the pointer
        const liveSeg = segs[seg];
        if (liveSeg) setHubLabel(liveSeg.label, liveSeg.win ? 0xffffff : 0x8b87a0);

        // final-stretch suspense
        if (!stateRef.current.turbo && !prefersReducedMotion() && tNorm >= SUSPENSE_FROM && tNorm < 1) {
          suspenseStrength = Math.min(1, (tNorm - SUSPENSE_FROM) / 0.08);
          if (!heartbeatPlayed) {
            heartbeatPlayed = true;
            playSfx('heartbeat');
          }
        }

        if (tNorm >= 1) {
          spinning = false;
          discRotation = spinTargetRotation; // snap exact
          if (env) {
            const landed = landedIndex != null ? segs[landedIndex] : null;
            if (landed) {
              setHubLabel(
                landed.label,
                env.win ? (Number.parseFloat(env.multiplier) >= BIG_WIN_MULTIPLIER ? GOLD : 0xffffff) : 0x8b87a0,
              );
            }
            startCelebration(env);
            fireReveal(env.roundId);
          }
        }
      } else if (ph === 'idle') {
        discRotation += IDLE_SPEED * dt;
        landedIndex = null;
        celebration = 'none';
        glow.clear();
        rays.clear();
        suspense.clear();
        setHubLabel('', 0xffffff);
        startedRoundId = null;
        firedRoundId = null;
      }

      drawSuspense(suspenseStrength);

      // celebration timeline
      if (celebration !== 'none' && (ph === 'playing' || ph === 'revealed')) {
        celebrationElapsed += dt;
        const p = Math.min(1, celebrationElapsed / CELEBRATE_MS);
        drawCelebrationGlow(p);
        drawRays(p);
      }

      disc.rotation = discRotation;

      // launch squash: tiny scale dip as the wheel takes off
      launchSquash = Math.max(0, launchSquash - dt / 450);
      const squash = 1 - 0.02 * Math.sin(launchSquash * Math.PI);
      disc.scale.set(squash);

      drawSheen(now, ph === 'idle');
      stepParticles(dt);
      stepCoins(dt);

      // ----- rim bulbs: chase while spinning, twinkle idle, flash on win -----
      const winFlash =
        (celebration === 'win' || celebration === 'bigwin')
          ? Math.max(0, 1 - celebrationElapsed / CELEBRATE_MS)
          : 0;
      const chaseSpeed = spinning ? 0.012 : 0.003;
      for (let i = 0; i < bulbs.length; i++) {
        const wave = Math.sin(now * chaseSpeed + i * 0.8);
        let alpha = 0.45 + 0.35 * wave;
        let scale = 0.85 + 0.2 * wave;
        if (winFlash > 0) {
          const flash = Math.abs(Math.sin(now * 0.02 + i));
          alpha = Math.min(1, alpha + winFlash * flash);
          scale = scale + winFlash * 0.5 * flash;
        }
        const b = bulbs[i];
        b.alpha = alpha;
        b.scale.set(scale);
        b.tint = winFlash > 0.4 ? (celebration === 'bigwin' ? GOLD : accentGlow) : 0xffffff;
      }

      // backdrop breathing / flare
      const flare = celebration === 'none' ? 0 : winFlash;
      const breathe = 1 + 0.03 * Math.sin(now / 700) + flare * 0.18;
      backdrop.scale.set((radius * 3.4 / 256) * breathe);
      backdrop.alpha = 0.32 + flare * 0.4;

      // hub pulse on win + label pop bounce
      labelPop = Math.max(0, labelPop - dt / 600);
      const pop = 1 + 0.45 * Math.sin(labelPop * Math.PI) * labelPop;
      hubLabel.scale.set(pop);
      hub.scale.set(1 + winFlash * 0.1 * Math.abs(Math.sin(now * 0.02)));

      // pointer ratchet: kicks against the spin direction, springs back upright
      pointerPulse = Math.max(0, pointerPulse - dt / 160);
      const idleBreathe = ph === 'idle' ? 0.04 * Math.sin(now / 600) : 0;
      pointer.scale.set(1 + pointerPulse * 0.1 + idleBreathe);
      pointer.rotation = -pointerPulse * 0.16;

      // screen shake (win jolt / lose kick), decays
      shake = Math.max(0, shake - dt * 0.06);
      if (shake > 0.1) {
        root.position.set(
          app.screen.width / 2 + (Math.sin(now * 0.08) * shake),
          app.screen.height / 2 + (Math.cos(now * 0.11) * shake),
        );
      } else {
        root.position.set(app.screen.width / 2, app.screen.height / 2);
      }
    };

    app.ticker.add(tick);

    void preloadPieces('wheel', WHEEL_PIECES, preferredDensity(window.devicePixelRatio)).then((imgs) => {
      if (!imgs || disposed) return;
      tex = toTextures(imgs);
      relayout();
    });

    return () => {
      disposed = true;
      if (tex) for (const t3d of Object.values(tex)) t3d.destroy(true);
      tex = null;
      app.ticker.remove(tick);
      app.renderer.off('resize', onResize);
    };
  }).current;

  // Start fetching the art with the component, ahead of the Pixi init the scene waits on.
  useEffect(() => {
    void preloadPieces('wheel', WHEEL_PIECES, preferredDensity(window.devicePixelRatio));
  }, []);

  const hostRef = usePixiScene(onReady);

  return (
    <div className="relative w-full lg:min-h-[clamp(340px,60vh,560px)] originals-scene-fit [--orig-reserve:620px] overflow-hidden">
      <div ref={hostRef} className="absolute inset-0" aria-hidden />
      {phase === 'idle' && (
        <span className="absolute bottom-3 rounded-full bg-black/35 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)] backdrop-blur-sm">
          {t('wheel.spin')}
        </span>
      )}
    </div>
  );
}
